/**
 * PL MULTI TotalSingle helpers — Client tree Σ + paper-registry WP detail Σ.
 * @see docs/multi-year/pl-multi-single-tab-toggle.md
 */

import {getComponentType} from '@/adjustment/adjust/utils/extractAdjustmentRows';
import {getWorkingPaper} from '@/services/auditService/auditWorkingPaperService';
import {useAdjustDraftStore} from '@/store/audit/adjustDraftStore';
import {
    getWpYoaMapCache,
    projectYoaBucketToWorkingPaperData,
} from '@/services/auditService/wpYoaMapCache';
import {
    applyMergedTaxAutoOntoTree,
    collectTaxAutoAdjustSumByFsId,
} from '@/pages/components/fs-report/plMultiTotalTaxAutoMerge';

export {
    applyMergedTaxAutoOntoTree,
    collectTaxAutoAdjustSumByFsId,
} from '@/pages/components/fs-report/plMultiTotalTaxAutoMerge';

export type PlMultiViewMode = 'multiple' | 'total';

/** Draft / periodScope sliceKey for TotalSingle WP preview — never write to real YOA buckets. */
export const PL_TOTAL_VIEW_SLICE_KEY = 'PL_TOTAL';

const AMOUNT_KEY_RE =
    /amount|client|final|adjust|debit|credit|cost|nbv|balance|persales|perclient|qty|quantity|price|income|expense|total|opening|closing|bf|cf|depn|amor|charge|value|sum|net/i;

/** Row-array field names that participate in adjustment (aligned with extractAdjustmentRows). */
export function getAdjustmentRowFieldNames(code: string): string[] {
    const type = getComponentType(code);
    switch (type) {
        case 'bank':
            return ['bankRow', 'commonRow'];
        case 'commonInvestment':
            return ['costRow'];
        case 'feesTransaction':
            return ['feesRow'];
        case 'debtTransaction':
            return ['debtRow'];
        case 'debtFeesTransaction':
            return ['debtFeeRow'];
        case 'dueFromToDirectors':
            return ['dueFromToDirRow'];
        case 'gainLossExchange':
            return ['gainExchange', 'lossExchange'];
        case 'gainLossDisposal':
            return [
                'locationOfPropertyDisRow',
                'commercialAllowanceDisRow',
                'industrialAllowanceDisRow',
                'furnitureFixtureDisRow',
                'computerHardSoftDisRow',
                'fullPayMotorDisRow',
                'hirePurchaseMotorDisRow',
                'machineryDisRow',
                'liftEscalatorDisRow',
                'equipmentDisRow',
                'subsidiaryRow',
                'associateRow',
                'intangibleRow',
                'taxiLicenseDisRow',
                'fullPayTaxiCabDisRow',
                'hirePurchaseTaxiCabDisRow',
                'trustsRow',
            ];
        case 'commissionPayment':
            return ['commissionPayments'];
        case 'withholdingTax':
            return ['whtRows'];
        case 'donation':
            return ['taxDeductible', 'nonTaxDeductible'];
        case 'purchases':
            return ['purchases'];
        case 'rentalExpense':
            return ['rows'];
        case 'salariesAllowance':
            return ['staffRows', 'workerRows', 'directorRows', 'dirQuarterRows'];
        case 'interestIncome':
            return ['bankInterestIncome', 'otherInterestIncome'];
        case 'salesIncome':
            return ['salesIncome'];
        case 'bankLoan':
            return ['bankLoans'];
        case 'vehicleLoan':
            return ['vehicleLoans'];
        case 'interestSummary':
            return [];
        case 'common':
        case 'commonTransaction':
        case 'commonMaterialTest':
        case 'inventories':
        case 'tax':
        case 'unknown':
        default:
            return ['commonRow'];
    }
}

function asArray(val: any): any[] {
    if (!val) return [];
    return Array.isArray(val) ? val : [val];
}

function rowMergeKey(row: any, index: number): string {
    if (row == null || typeof row !== 'object') return `__idx_${index}`;
    const candidates = [
        row.rowId,
        row.id,
        row.uuid,
        row.glId,
        row.categoryId,
        row.ppeRowId,
        row.itemId,
    ];
    for (const c of candidates) {
        if (c !== undefined && c !== null && String(c).trim() !== '') {
            return `id:${String(c)}`;
        }
    }
    const nameBits = [row.name, row.item, row.description, row.particular, row.bankName, row.tenant]
        .map((x) => (x == null ? '' : String(x).trim()))
        .filter(Boolean);
    if (nameBits.length > 0) return `name:${nameBits.join('|')}`;
    return `__idx_${index}`;
}

function isSummableNumber(key: string, value: unknown): value is number {
    if (typeof value !== 'number' || !Number.isFinite(value)) return false;
    if (key === 'rowId' || key === 'id' || key === 'fsId' || key === 'lineId' || key === 'headerId') {
        return false;
    }
    return AMOUNT_KEY_RE.test(key);
}

function mergeRowPair(a: any, b: any): any {
    if (!a || typeof a !== 'object') return b;
    if (!b || typeof b !== 'object') return a;
    const out: Record<string, any> = {...a};
    for (const [key, bVal] of Object.entries(b)) {
        const aVal = out[key];
        if (isSummableNumber(key, aVal) && isSummableNumber(key, bVal)) {
            out[key] = aVal + bVal;
        } else if (isSummableNumber(key, bVal) && (aVal === undefined || aVal === null || aVal === '')) {
            out[key] = bVal;
        } else if ((aVal === undefined || aVal === null || aVal === '') && bVal !== undefined && bVal !== null) {
            out[key] = bVal;
        }
        // else keep a (first non-empty wins for non-amount)
    }
    return out;
}

function mergeRowArrays(arrays: any[][]): any[] {
    const map = new Map<string, any>();
    const order: string[] = [];
    for (const arr of arrays) {
        asArray(arr).forEach((row, index) => {
            const key = rowMergeKey(row, index);
            if (!map.has(key)) {
                map.set(key, {...(row && typeof row === 'object' ? row : {value: row})});
                order.push(key);
            } else {
                map.set(key, mergeRowPair(map.get(key), row));
            }
        });
    }
    return order.map((k) => map.get(k));
}

/**
 * Merge WP details across period slices: adjustment row fields summed by row key;
 * non-row metadata from first non-empty detail.
 */
export function aggregateWpDetailsAcrossSlices(code: string, details: any[]): any | null {
    const list = (details || []).filter((d) => d && typeof d === 'object');
    if (list.length === 0) return null;
    if (list.length === 1) {
        try {
            return structuredClone(list[0]);
        } catch {
            return JSON.parse(JSON.stringify(list[0]));
        }
    }

    const base: Record<string, any> = {};
    // Prefer first detail's non-row keys
    for (const [k, v] of Object.entries(list[0])) {
        base[k] = v;
    }

    const fields = getAdjustmentRowFieldNames(code);
    const fieldSet = new Set(fields.length > 0 ? fields : ['commonRow']);

    for (const field of fieldSet) {
        const arrays = list.map((d) => asArray(d?.[field]));
        if (arrays.some((a) => a.length > 0)) {
            base[field] = mergeRowArrays(arrays);
        }
    }

    // Sum top-level numeric amount-like fields across details
    for (const detail of list.slice(1)) {
        for (const [key, val] of Object.entries(detail)) {
            if (fieldSet.has(key)) continue;
            if (isSummableNumber(key, base[key]) && isSummableNumber(key, val)) {
                base[key] = base[key] + val;
            }
        }
    }

    return base;
}

export async function loadAndAggregatePlWpDetails(opts: {
    code: string;
    slices: Array<{key: string; yearOfAssessment?: string | null}>;
    companyId: number;
    annualId: number;
    workflow?: string;
}): Promise<any | null> {
    const {code, slices, companyId, annualId, workflow} = opts;
    const normalized = String(code || '').trim().toUpperCase();
    if (!normalized) return null;

    const draft = useAdjustDraftStore.getState();
    const details: any[] = [];
    const pushDetail = (raw: any) => {
        const unwrapped = unwrapWpDetailBag(raw);
        if (unwrapped && typeof unwrapped === 'object') {
            details.push(unwrapped);
        }
    };

    // --- Prefer BS MULTI wire: one GET ?yoaMap=true (no dedicated Σ API on backend) ---
    try {
        await getWorkingPaper({
            companyId,
            annualId,
            code: normalized,
            yoaMap: true,
            ...(workflow ? {workflow} : {}),
        } as any);
        const cache = getWpYoaMapCache(companyId, annualId, normalized);
        if (cache && Array.isArray(cache.yoaOrder) && cache.yoaOrder.length > 0) {
            for (const yoa of cache.yoaOrder) {
                const slice = (slices || []).find(
                    (s) => String(s.yearOfAssessment || '').trim() === String(yoa).trim(),
                );
                const draftKey = slice ? `${normalized}__${slice.key}` : null;
                const fromDraft = draftKey ? draft.getUnifiedDetail?.(draftKey) : null;
                if (fromDraft) {
                    pushDetail(fromDraft);
                } else {
                    pushDetail(projectYoaBucketToWorkingPaperData(cache, yoa));
                }
            }
        }
    } catch {
        // fall through to per-slice path
    }

    // --- Fallback: draft / per-YOA get (pl-slice shape) ---
    if (details.length === 0 && Array.isArray(slices) && slices.length > 0) {
        for (const slice of slices) {
            const draftKey = `${normalized}__${slice.key}`;
            let detail = draft.getUnifiedDetail?.(draftKey) ?? null;
            if (!detail) {
                try {
                    const yoa = slice.yearOfAssessment ? String(slice.yearOfAssessment).trim() : '';
                    const papers = await getWorkingPaper({
                        companyId,
                        annualId,
                        code: normalized,
                        ...(yoa ? {yearOfAssessment: yoa} : {}),
                        ...(workflow ? {workflow} : {}),
                    } as any);
                    const first = Array.isArray(papers) ? papers[0] : papers;
                    detail = first ?? null;
                } catch {
                    detail = null;
                }
            }
            pushDetail(detail);
        }
    }

    return aggregateWpDetailsAcrossSlices(normalized, details);
}

/** Normalize WorkingPaperData / bucket / flat detail into a mergeable bag. */
function unwrapWpDetailBag(raw: any): any | null {
    if (!raw || typeof raw !== 'object') return null;
    if (Array.isArray(raw.details) && raw.details[0] && typeof raw.details[0] === 'object' && !Array.isArray(raw.details[0])) {
        return {
            ...raw.details[0],
            ...(raw.wpStatus != null ? {wpStatus: raw.wpStatus} : {}),
            ...(raw.wpIssueRemark != null ? {wpIssueRemark: raw.wpIssueRemark} : {}),
            ...(raw.outstanding != null ? {outstanding: raw.outstanding} : {}),
            ...(raw.outstandingRemark != null ? {outstandingRemark: raw.outstandingRemark} : {}),
            ...(raw.glInvoiceDetails != null ? {glInvoiceDetails: raw.glInvoiceDetails} : {}),
        };
    }
    if (raw.details && typeof raw.details === 'object' && !Array.isArray(raw.details)) {
        return {...raw.details};
    }
    return raw;
}

/**
 * Build PL tree for TotalSingle: openingAmount/client = Σ across period slices (per fsId).
 * Prefer engagement plLine as template (Adjust papers intact); else first period tree.
 * TAX Auto: Σ across YOA period trees onto each leaf (combine / Total view).
 */
export function buildTotalPlClientTree(
    periods: Array<{key?: string; yearOfAssessment?: string | null; plLine?: any[]} | null | undefined> | null | undefined,
    engagementPlLine?: any[] | null,
    convertForYoa?: (vo: any, yoa: string) => any,
): any[] | null {
    const periodList = (periods || []).filter((p) => p && Array.isArray(p.plLine) && (p.plLine as any[]).length > 0) as Array<{
        yearOfAssessment?: string | null;
        plLine: any[];
    }>;

    if (periodList.length === 0) {
        return Array.isArray(engagementPlLine) ? engagementPlLine.map(shallowCloneNode) : null;
    }

    const clientSumByFsId = new Map<number, number>();
    const periodTrees: any[][] = [];

    const walkSum = (nodes: any[]) => {
        for (const n of nodes || []) {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                walkSum(children);
                continue;
            }
            const fsId = Number(n?.fsId);
            if (!Number.isFinite(fsId) || fsId <= 0) continue;
            const client = Number(n?.openingAmount ?? n?.client ?? n?.amount ?? 0) || 0;
            clientSumByFsId.set(fsId, (clientSumByFsId.get(fsId) || 0) + client);
        }
    };

    // Dedupe by YOA: convertForYoa pulls the same amountsByYearOfAssessment bucket per YOA.
    // If two period slices share one YOA (e.g. REGULAR anniversary collision), summing per-slice
    // would double Client — skip already-seen YOAs.
    const seenYoa = new Set<string>();
    for (const p of periodList) {
        const yoa = String(p.yearOfAssessment || '').trim();
        if (yoa) {
            if (seenYoa.has(yoa)) continue;
            seenYoa.add(yoa);
        }
        const tree = convertForYoa && yoa
            ? (p.plLine || []).map((vo) => convertForYoa(vo, yoa))
            : (p.plLine || []);
        periodTrees.push(tree);
        walkSum(tree);
    }

    // Engagement tree keeps full Adjust column set; period[0] is fallback structure only
    let template: any[];
    if (Array.isArray(engagementPlLine) && engagementPlLine.length > 0) {
        template = engagementPlLine.map(shallowCloneNode);
    } else {
        const templateSource = periodList[0].plLine;
        const templateYoa = String(periodList[0].yearOfAssessment || '').trim();
        template = convertForYoa && templateYoa
            ? templateSource.map((vo) => convertForYoa(vo, templateYoa))
            : templateSource.map(shallowCloneNode);
    }

    const applySum = (nodes: any[]): any[] =>
        (nodes || []).map((n) => {
            const children = Array.isArray(n?.children) ? n.children : [];
            if (children.length > 0) {
                const nextChildren = applySum(children);
                const level = n.level || 0;
                let opening = n.openingAmount;
                if (level === 2) {
                    opening = nextChildren.reduce(
                        (s: number, c: any) => s + (Number(c.openingAmount) || 0),
                        0,
                    );
                }
                return {...n, children: nextChildren, openingAmount: opening};
            }
            const fsId = Number(n?.fsId);
            const summed = clientSumByFsId.get(fsId);
            return {
                ...n,
                openingAmount: summed !== undefined ? summed : (Number(n?.openingAmount) || 0),
            };
        });

    const withClientSum = applySum(template);
    const taxByFsId = collectTaxAutoAdjustSumByFsId(periodTrees);
    return applyMergedTaxAutoOntoTree(withClientSum, taxByFsId);
}

function shallowCloneNode(vo: any): any {
    const children = Array.isArray(vo?.children) ? vo.children.map(shallowCloneNode) : [];
    return {...vo, children};
}

export function isPlTotalViewSliceKey(sliceKey?: string | null): boolean {
    return String(sliceKey || '').trim() === PL_TOTAL_VIEW_SLICE_KEY;
}
